import { useEffect, useMemo } from 'react';

import useForceUpdate from './useForceUpdate';

const extractRect = (data) => (data?.getBoundingClientRect ? data.getBoundingClientRect() : data);

/**
 * Position
 * @typedef {object} Position
 *
 * @property {number} top
 * @property {number} left
 */

/**
 * @param {Position} position
 * @param {object} elements
 * @param {HTMLElement | object} elements.target HTMLElement or object that resembles element's rect
 * @param {HTMLElement} elements.mountEl HTMLElement
 * @param {object} params
 * @param {number} params.threshold minimum threshold that the target element should be distanced from the mount element
 * @param {'center' | 'bottom'} params.placement the placement to anchor from
 *
 * @returns {Position} position
 */
export const normalizeElementPosition = (
  position,
  { target, mountEl, isNormalize = true } = {},
  { threshold = 5 } = {},
) => {
  const targetRect = extractRect(target);
  const xRightOverflow = position.left + targetRect.width - mountEl.clientWidth + threshold;
  const normalizedLeftByXRight = xRightOverflow > 0 ? position.left - xRightOverflow : position.left;
  const xLeftOverflow = normalizedLeftByXRight - threshold;
  const normalizedLeftByXLeft = xLeftOverflow < 0 ? threshold : normalizedLeftByXRight;

  return {
    top: position.top,
    left: isNormalize ? normalizedLeftByXLeft : position.left,
  };
};

/**
 * @param {object} elements
 * @param {HTMLElement | object} elements.target HTMLElement or object that resembles element's rect
 * @param {HTMLElement | object} elements.anchor HTMLElement or object that resembles element's rect
 * @param {HTMLElement} elements.mountEl HTMLElement
 * @param {object} params
 * @param {number} params.shiftY the value to shift vertically
 * @param {'center' | 'bottom' | 'bottom-center'} params.placement the placement to anchor from
 *
 * @returns {Position} position
 */
export const getElementPosition = (
  { target, mountEl, anchor } = {},
  { shiftY = 0, shiftX = 0, placement = 'center' } = {},
) => {
  const targetRect = extractRect(target);
  const anchorRect = extractRect(anchor);

  if (placement === 'center') {
    if (!mountEl) {
      return {
        top: anchorRect.top + anchorRect.height / 2 - targetRect.height / 2 + shiftY,
        left: anchorRect.left + anchorRect.width / 2 - targetRect.width / 2 + shiftX,
      };
    }

    return {
      top:
        anchorRect.top +
        anchorRect.height / 2 -
        targetRect.height / 2 -
        mountEl.getBoundingClientRect().top +
        mountEl.scrollTop +
        shiftY,
      left:
        anchorRect.left + anchorRect.width / 2 - targetRect.width / 2 - mountEl.getBoundingClientRect().left + shiftX,
    };
  }

  if (placement === 'bottom') {
    if (!mountEl) {
      return {
        top: anchorRect.top + anchorRect.height + shiftY,
        left: anchorRect.left + shiftX,
      };
    }

    return {
      top: anchorRect.top + anchorRect.height - mountEl.getBoundingClientRect().top + shiftY,
      left: anchorRect.left - mountEl.getBoundingClientRect().left + shiftX,
    };
  }

  if (placement === 'bottom-center') {
    if (!mountEl) {
      return {
        top: anchorRect.top + anchorRect.height + shiftY,
        left: anchorRect.left + anchorRect.width / 2 - targetRect.width / 2 + shiftX,
      };
    }

    return {
      top: anchorRect.top + anchorRect.height - mountEl.getBoundingClientRect().top + mountEl.scrollTop + shiftY,
      left:
        anchorRect.left + anchorRect.width / 2 - targetRect.width / 2 - mountEl.getBoundingClientRect().left + shiftX,
    };
  }

  if (placement === 'left') {
    console.dir({
      mountEl,
    });
    if (!mountEl) {
      return {
        top: anchorRect.top + anchorRect.height + shiftY,
        left: anchorRect.left + anchorRect.width / 2 - targetRect.width / 2 + shiftX,
      };
    }

    return {
      top: anchorRect.top - mountEl.getBoundingClientRect().top + mountEl.scrollTop + shiftY,
      left: anchorRect.left - targetRect.width - mountEl.getBoundingClientRect().left + shiftX,
    };
  }
};

const useElementPosition = (elements = {}, params) => {
  const { targetRef, mountEl, anchorEl } = elements;
  const { isNormalize, relativeEl } = params;
  const forceUpdate = useForceUpdate();
  const targetEl = targetRef?.current;

  // we dont want to recalculate element position
  // element position is determined on element mounting
  const targetStyles = useMemo(() => {
    return targetEl
      ? normalizeElementPosition(
          getElementPosition(
            {
              mountEl,
              anchor: anchorEl,
              target: targetEl,
            },
            params,
          ),
          {
            mountEl,
            relativeEl,
            target: targetEl,
            isNormalize,
          },
        )
      : {
          // hide element until we get access to target ref
          visibility: 'hidden',
          opacity: 0,
        };
  }, [targetEl]);

  useEffect(() => {
    // use force update to get ref element
    forceUpdate();
  }, []);

  return {
    targetStyles,
    isShown: targetStyles.visibility !== 'hidden',
  };
};

export default useElementPosition;
